import { Button, FormikField, ModalStyle as Modal, toastSuccess } from "@hubrise/react-components"
import type { InputRef } from "@hubrise/react-components"
import { Form, Formik } from "formik"
import type { FormikErrors } from "formik"
import * as React from "react"
import { useTranslation } from "react-i18next"

import { useDuplicateCatalogMutation } from "shared/hooks/mutations"
import { ICatalog, ICatalogSummary } from "shared/hubrise/models"

interface DuplicateModalContentProps {
  catalogSummary: ICatalogSummary
  catalogSummaries: Array<ICatalogSummary>
  catalog?: ICatalog
  setDuplicationInProgress: (state: boolean) => void
  close: () => void
}

const DuplicateModalContent = ({
  catalogSummary,
  catalogSummaries,
  catalog,
  setDuplicationInProgress,
  close,
}: DuplicateModalContentProps): JSX.Element => {
  const { t } = useTranslation()

  const $nameRef = React.useRef<InputRef>(null)

  React.useEffect(() => $nameRef.current?.focus())
  const handleFocus = (): void => $nameRef.current?.select()

  type FormValues = { name: string }
  const initialValues: FormValues = { name: catalogSummary.name }

  const validate = ({ name }: FormValues): FormikErrors<FormValues> => {
    const errors: FormikErrors<FormValues> = {}
    if (name.length === 0) errors.name = t("validations.not_empty")
    if (catalogSummaries.map((s) => s.name).includes(name)) errors.name = t("validations.must_be_unique")
    return errors
  }

  const mutation = useDuplicateCatalogMutation({
    onSuccess: (_, { name }) => {
      setDuplicationInProgress(false)
      toastSuccess(t("catalogs.actions.duplicate.success", { name }))
    },
  })

  const handleSubmit = ({ name }: FormValues) => {
    const locationHrId = undefined // TODO: support location level catalogs
    setDuplicationInProgress(true)
    mutation.mutate({ catalogOrId: catalog || catalogSummary.id, locationHrId, name })
    close()
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validate={validate}>
      <Form>
        <Modal.Header>
          <Modal.Title>{t("catalogs.actions.duplicate.title")}</Modal.Title>
          <Modal.Close onClick={close} />
        </Modal.Header>

        <Modal.Body>
          <FormikField.Input
            ref={$nameRef}
            name="name"
            label={t("catalogs.actions.duplicate.name")}
            withMargin
            onFocus={handleFocus}
          />
        </Modal.Body>

        <Modal.Footer>
          <Button variant="primary" type="submit">
            {t("catalogs.actions.duplicate.action")}
          </Button>
          <Button variant="empty" onClick={close}>
            {t("general.cancel")}
          </Button>
        </Modal.Footer>
      </Form>
    </Formik>
  )
}

export default DuplicateModalContent
